import { CABINET_QR_SCAN } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BoxResponse,
  BoxService,
  CellCoord,
  ItemResponse,
  ItemService,
  StorageStatus,
  WarehouseResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { NotButton, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { parseQrToBoxOrItemId } from '../utils/qr-storage-entity.util';
import { isWarehouseUuid } from '../utils/warehouse-route.util';
import { StaffWarehouseCellPickerComponent } from './staff-warehouse-cell-picker.component';
import { cellCoordsFromRefs } from './staff-warehouse-cell-picker.util';

type PlacementTarget =
  | { kind: 'box'; entity: BoxResponse }
  | { kind: 'item'; entity: ItemResponse };

@Component({
  selector: 'lib-staff-warehouse-placement',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NotButton, StaffWarehouseCellPickerComponent],
  templateUrl: './staff-warehouse-placement.component.html',
  styleUrl: './staff-warehouse-placement.component.scss',
})
export class StaffWarehousePlacementComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #route = inject(ActivatedRoute);
  readonly #translate = inject(TranslateService);
  readonly #warehouseService = inject(WarehouseService);
  readonly #boxService = inject(BoxService);
  readonly #itemService = inject(ItemService);
  readonly #qrScan = inject(CABINET_QR_SCAN);
  readonly #notification = inject(NotSystemNotificationService);

  protected readonly warehouseId = signal('');
  protected readonly warehouseName = signal('');
  protected readonly warehouse = signal<WarehouseResponse | null>(null);
  protected readonly loadingWarehouse = signal(true);
  protected readonly warehouseError = signal<string | null>(null);

  protected readonly hasWarehouseGrid = computed(
    () => (this.warehouse()?.rows?.length ?? 0) > 0,
  );

  protected readonly target = signal<PlacementTarget | null>(null);
  protected readonly loadingTarget = signal(false);
  protected readonly scanning = signal(false);
  protected readonly submitting = signal(false);

  protected readonly pickerInitialCells = computed(() => {
    const target = this.target();
    if (!target) {
      return [];
    }
    return cellCoordsFromRefs(target.entity.cells);
  });

  public ngOnInit(): void {
    const segment =
      this.#route.snapshot.paramMap.get('slug') ??
      this.#route.snapshot.paramMap.get('id');
    if (!segment) {
      this.warehouseError.set(
        this.#translate.instant('admin.warehouses.detailNotFound'),
      );
      this.loadingWarehouse.set(false);
      return;
    }

    const loadWarehouseByUuid = (uuid: string): void => {
      this.warehouseId.set(uuid);
      this.#warehouseService.getWarehouse(uuid).subscribe({
        next: (warehouse) => {
          this.warehouse.set(warehouse);
          this.warehouseName.set(warehouse.name);
          this.loadingWarehouse.set(false);
          const prefBox = this.#route.snapshot.queryParamMap.get('box');
          const prefItem = this.#route.snapshot.queryParamMap.get('item');
          if (prefBox) {
            this.#loadBoxById(prefBox);
          } else if (prefItem) {
            this.#loadItemById(prefItem);
          }
        },
        error: () => {
          this.warehouseError.set(
            this.#translate.instant('admin.warehouses.placement.loadWarehouseError'),
          );
          this.loadingWarehouse.set(false);
        },
      });
    };

    if (isWarehouseUuid(segment)) {
      loadWarehouseByUuid(segment);
    } else {
      this.#warehouseService.getWarehouseBySlug(segment).subscribe({
        next: (warehouse) => loadWarehouseByUuid(warehouse.id),
        error: () => {
          this.warehouseError.set(
            this.#translate.instant('admin.warehouses.placement.loadWarehouseError'),
          );
          this.loadingWarehouse.set(false);
        },
      });
    }
  }

  protected async onScanQr(): Promise<void> {
    if (this.scanning()) {
      return;
    }
    this.scanning.set(true);
    try {
      const outcome = await this.#qrScan.scanQrCode();
      if (outcome.kind === 'unsupportedEnvironment') {
        this.#toastError('admin.warehouses.placement.scanUnsupported');
        return;
      }
      if (outcome.kind === 'canceled') {
        this.#toastError('admin.warehouses.placement.scanCanceled');
        return;
      }
      if (outcome.kind === 'empty') {
        this.#toastError('admin.warehouses.placement.scanEmpty');
        return;
      }
      if (outcome.kind === 'error') {
        this.#toastError('admin.warehouses.placement.scanError');
        return;
      }
      const parsed = parseQrToBoxOrItemId(outcome.value);
      if (!parsed) {
        this.#toastError('admin.warehouses.placement.wrongQr');
        return;
      }
      if ('user_id' in parsed) {
        this.#toastError('admin.warehouses.placement.wrongQr');
        return;
      }
      if ('cell_id' in parsed) {
        this.#toastError('admin.warehouses.placement.wrongQr');
        return;
      }
      if ('item_id' in parsed) {
        this.#loadItemById(parsed.item_id);
        return;
      }
      if ('box_id' in parsed) {
        this.#loadBoxById(parsed.box_id);
      }
    } finally {
      this.scanning.set(false);
    }
  }

  #loadBoxById(boxId: string): void {
    this.loadingTarget.set(true);
    this.target.set(null);
    this.#boxService.getAdminBox(boxId).subscribe({
      next: (box) => {
        this.target.set({ kind: 'box', entity: box });
        this.loadingTarget.set(false);
      },
      error: () => {
        this.loadingTarget.set(false);
        this.#toastError('admin.warehouses.placement.boxLoadError');
      },
    });
  }

  #loadItemById(itemId: string): void {
    this.loadingTarget.set(true);
    this.target.set(null);
    this.#itemService.getAdminItem(itemId).subscribe({
      next: (item) => {
        if (!item.is_standalone || item.box_id) {
          this.loadingTarget.set(false);
          this.#toastError('admin.warehouses.placement.itemNotStandalone');
          return;
        }
        if (item.status !== StorageStatus.Stored) {
          this.loadingTarget.set(false);
          this.#toastError('admin.warehouses.placement.itemNotStored');
          return;
        }
        this.target.set({ kind: 'item', entity: item });
        this.loadingTarget.set(false);
      },
      error: () => {
        this.loadingTarget.set(false);
        this.#toastError('admin.warehouses.placement.itemLoadError');
      },
    });
  }

  protected onPickerSubmit(cells: CellCoord[]): void {
    const warehouseId = this.warehouseId();
    const target = this.target();
    if (!warehouseId || !target) {
      this.#toastError('admin.warehouses.placement.needsTarget');
      return;
    }
    if (!this.hasWarehouseGrid()) {
      this.#toastError('admin.warehouses.placement.noGridConfig');
      return;
    }

    this.submitting.set(true);
    const body = { warehouse_id: warehouseId, cells };
    const request$ =
      target.kind === 'box'
        ? this.#boxService.placeBoxOnCell(target.entity.id, body)
        : this.#itemService.placeItemOnCells(target.entity.id, body);

    request$.subscribe({
      next: (response) => {
        this.submitting.set(false);
        const addresses =
          response.cells?.map((cell) => cell.address).join(', ') ||
          response.address ||
          '';
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [
            this.#translate.instant('admin.warehouses.placement.success', {
              address: addresses,
            }),
          ],
        });
        this.target.set(null);
      },
      error: (err: HttpErrorResponse) => {
        this.submitting.set(false);
        const bodyErr = err.error as { error?: string } | null;
        const message =
          bodyErr?.error ??
          this.#translate.instant('admin.warehouses.placement.submitError');
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [message],
        });
      },
    });
  }

  protected targetAddressLabel(): string | null {
    const target = this.target();
    if (!target) {
      return null;
    }
    const cells = target.entity.cells;
    if (cells?.length) {
      return cells.map((cell) => cell.address).join(', ');
    }
    if (target.kind === 'box') {
      return target.entity.location_code ?? null;
    }
    return null;
  }

  #toastError(key: string): void {
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [this.#translate.instant(key)],
    });
  }
}
